import React from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import {
  getBlackoutDates,
  i18nTranslate,
  isToEnableSubscriptionOverride,
  isToSkipSubscriptionOrderForOverride,
  getLocaleCodeFromUrl,
  isFeatureEnabled,
  getTimeZone,
  IS_BROWSER,
  trackErrorInInstana,
  isCSRActOnBehalfSession,
  is3DSEnabledForGPS,
  deletePaymentById,
  handleGPS3DSRedirectStatus,
  triggerGPS3DSInitiateCall,
} from 'src/utils'
import { pageNames } from 'src/routes/pathParams'
import { MicroFrontendLoader } from 'src/views/components/MicroFrontends/MicroFrontendLoader'
import { SubscriptionInfiniteScrollV2 } from 'src/views/components/SubscriptionInfiniteScrollV2'
import { SubscriptionModal } from './SubscriptionModal'
import {
  customerContainer,
  accountsContainer,
  storeContainer,
  addressContainer,
  subscriptionContainer,
  multipleSubscriptionContainer,
  checkoutContainer,
  cartContainer,
} from 'src/models'
import { overlayState, toastState } from 'src/views/components'
import { BreadCrumb, MetaDecorator } from 'src/views/components'
import { getAllowedSubscriptionFrequency } from 'src/deps/NuskinSubscriptionDeps'
import { APPConfig, convertToBoolean } from 'config/appConfig'
import { showAllPromoCodesInCheckout } from 'config/appConfig/configUtils'
import './styles.scss'
import { EditBundleMultiVariants } from 'src/views/components/FlexibleSubscription'
import { enableSubscriptionMultiVariant } from 'src/deps/SubscriptionDeps.js'
import { nextGenTranslationContent } from 'src/deps/NextGenTranslationContent'
import { getMysiteSponsor, isEquinoxMysite } from 'src/utils/mySiteUtils'
import {
  getSubscriptionLimitConfig,
  getSubscriptionAuthorizedLimit,
} from 'src/utils/multipleSubscriptionUtils'
import { scrollUtilities } from 'src/utils/customScrollManager'

@observer
class MultipleSubscriptions extends React.Component {
  @observable subscriptionsName = ''
  @observable isManagePage = false
  @observable microFrontendKey = Date.now()
  @observable globalPaymentResponse = {}
  @observable isGPS3DSEnabledInSubscription = false
  shouldReloadAfterSuccess = false
  containerRef = React.createRef()
  // Set by triggerSubscription3DSInitiateCall right before redirecting to the GPS
  // gateway; only handleSubscription3DSRedirectStatus clears them. Their presence
  // is the reliable signal that a landing belongs to the GPS 3DS card flow (which
  // shares the same orderId/gps_status query params as the eMandate redirect).
  subscription3DSPaymentIdSessionKey = 'multipleSub3ds_paymentId'
  subscription3DSOrderIdSessionKey = 'multipleSub3ds_orderId'
  // Handed to the subscription MFE, which mounts after this page's
  // componentDidMount and would miss a pubsub event published here.
  subscription3DSApprovedPaymentSessionKey = 'multipleSub3ds_approvedPaymentId'

  componentDidMount = async () => {
    // handleEMandateRedirect must read orderId/gps_status from the URL before
    // handleSubscription3DSRedirectStatus strips those same params via
    // history.replaceState, or the eMandate activation silently no-ops.
    await this.handleEMandateRedirect()
    await this.handleSubscription3DSRedirectStatus()
    this.initializeData()
    scrollUtilities.scrollToTop('my-account-details-wrapper')

    if (this.containerRef.current) {
      this.containerRef.current.focus()
    }
  }

  // Invoked on the "Add Payment" click (not on page load) so the subscription
  // dashboard does not fire globalPaymentOptions until payment options are needed.
  getGlobalPaymentOptionsForSubscription = async () => {
    // args: (isSubscriptionProductInCart, isFromPDP) - neither is a force-refresh flag.
    const globalPaymentOptionsResponse =
      await checkoutContainer.getGlobalPaymentOptions(true, true)
    this.globalPaymentResponse =
      globalPaymentOptionsResponse?.response ||
      checkoutContainer?.globalPaymentResponse ||
      {}
    const validProcessorObj = checkoutContainer.getValidGPSCreditCardProcessor()
    this.isGPS3DSEnabledInSubscription = is3DSEnabledForGPS(validProcessorObj)
  }

  deleteSubscriptionPayment = async paymentId => {
    await deletePaymentById({
      paymentId,
      fetchResponse: params => customerContainer.fetchResponse(params),
    })
  }

  triggerSubscription3DSInitiateCall = async paymentId => {
    return triggerGPS3DSInitiateCall({
      paymentId,
      isGPS3DSEnabled: this.isGPS3DSEnabledInSubscription,
      isCSRActOnBehalf: isCSRActOnBehalfSession(),
      deletePaymentById: this.deleteSubscriptionPayment,
      fetchResponse: params => checkoutContainer.fetchResponse(params),
      isSuccessResponse: response =>
        checkoutContainer.isSuccessResponse(response),
      storeId: APPConfig.getAppConfig()?.storeId,
      paymentIdSessionKey: this.subscription3DSPaymentIdSessionKey,
      orderIdSessionKey: this.subscription3DSOrderIdSessionKey,
      initiateEndPointName: 'addAccountPaymentForPaymetric3ds',
    })
  }

  handleSubscription3DSRedirectStatus = async () => {
    const redirectStatus = await handleGPS3DSRedirectStatus({
      paymentIdSessionKey: this.subscription3DSPaymentIdSessionKey,
      orderIdSessionKey: this.subscription3DSOrderIdSessionKey,
      deletePaymentById: this.deleteSubscriptionPayment,
      fetchResponse: params => checkoutContainer.fetchResponse(params),
      isSuccessResponse: response =>
        checkoutContainer.isSuccessResponse(response),
      storeId: APPConfig.getAppConfig()?.storeId,
      zeroDollarAuthEndPointName: 'addAccountPaymentForPaymetric3ds',
    })
    if (redirectStatus?.isApproved && redirectStatus?.paymentId) {
      sessionStorage.setItem(
        this.subscription3DSApprovedPaymentSessionKey,
        redirectStatus.paymentId
      )
    }
  }

  getNextGentTranslationContent = async () => {
    const curLocale = getLocaleCodeFromUrl({
      isReverseType: true,
      defaultLocale: 'en_US',
    })
    const translationObj = {
      locale: curLocale,
      contentTypeId: 'nextgen_subscription',
      contentType: 'subscription',
    }

    await nextGenTranslationContent.fetchNextGenTranslationContent(
      translationObj
    )
  }

  /**
   * Handles eMandate redirection from Razorpay payment gateway.
   * Checks for orderId and gps_status=APPROVED query parameters,
   * retrieves subscriptionId and gpsOrderId from sessionStorage,
   * and calls the updateSubscriptionEMandateStatus API.
   */
  handleEMandateRedirect = async () => {
    try {
      if (!IS_BROWSER) {
        return
      }

      const urlParams = new URLSearchParams(window.location.search)
      const orderId = urlParams.get('orderId')
      const gpsStatus = urlParams.get('gps_status')

      // A GPS 3DS card redirect shares these same orderId/gps_status params. If
      // its session marker is present, this landing belongs to that flow, not
      // eMandate - bail out untouched and let handleSubscription3DSRedirectStatus
      // read and clean up the URL.
      const isGPS3DSCardRedirectInProgress = !!sessionStorage.getItem(
        this.subscription3DSPaymentIdSessionKey
      )

      // Check if this is an eMandate redirect with APPROVED status
      if (
        orderId &&
        gpsStatus === 'APPROVED' &&
        !isGPS3DSCardRedirectInProgress
      ) {
        // Retrieve stored data from sessionStorage
        const subscriptionId = sessionStorage.getItem('emandate_subscriptionId')
        const gpsOrderId = sessionStorage.getItem('emandate_gpsOrderId')
        const productDataStr = sessionStorage.getItem('emandate_productData')
        const productName = sessionStorage.getItem('emandate_productName')

        // If no sessionStorage data, we've already processed this callback
        if (!subscriptionId || !gpsOrderId) {
          // Already processed, just clean up URL
          const currentUrlParams = new URLSearchParams(window.location.search)
          const id = currentUrlParams.get('id')
          const cleanUrl = id
            ? `${window.location.pathname}?id=${id}`
            : window.location.pathname
          window.history.replaceState({}, document.title, cleanUrl)
          return
        }
        // Start processing - parse productData first, then remove sessionStorage to prevent re-execution
        const productData = productDataStr ? JSON.parse(productDataStr) : null
        // Remove sessionStorage data immediately to prevent re-execution
        sessionStorage.removeItem('emandate_subscriptionId')
        sessionStorage.removeItem('emandate_gpsOrderId')
        sessionStorage.removeItem('emandate_productData')
        sessionStorage.removeItem('emandate_productName')
        overlayState.showLoader()
        try {
          const storeId =
            storeContainer?.activeStoreId || storeContainer?.storeIDValue || ''
          const statusResponse =
            await multipleSubscriptionContainer.updateSubscriptionEMandateStatus(
              subscriptionId,
              gpsOrderId,
              storeId
            )
          // If status update is successful, add the product to subscription
          if (multipleSubscriptionContainer.isSuccessResponse(statusResponse)) {
            if (productData) {
              try {
                const addToSubscriptionResponse =
                  await multipleSubscriptionContainer.quickAddToSubscription({
                    data: productData,
                    subscriptionId: subscriptionId,
                    successCallBack: () => {
                      console.log(
                        'QuickAdd successCallBack triggered - Product added successfully!'
                      )
                      this.microFrontendKey = Date.now() // Force reload Microfrontend
                      let successMessage
                      if (productName) {
                        const itemAddedText = i18nTranslate(
                          'cartCreateNewSubscriptionComponent.addToSubscription.subscribedSuccessfullyToaster',
                          'You have successfully subscribed to %%product_name%%',
                          { isNextGenTranslations: true }
                        ).replace('%%product_name%%', productName)
                        successMessage = itemAddedText
                      } else {
                        successMessage = i18nTranslate(
                          'subscription.successItem',
                          'Item(s) successfully added to subscription'
                        )
                      }
                      toastState.setToastMessage(successMessage, true)
                    },
                  })
              } catch (quickAddError) {
                console.error(
                  'Error calling quickAddToSubscription:',
                  quickAddError
                )
                toastState.setToastMessage(
                  i18nTranslate(
                    'subscription.quickAddFailure',
                    'Unable to add item to subscription. Please try again.'
                  ),
                  false
                )
              }
            }
          } else {
            const errorMessage =
              statusResponse?.message ||
              i18nTranslate(
                'subscription.eMandateStatusUpdateFailure',
                'Unable to verify payment status. Please try again.'
              )
            toastState.setToastMessage(errorMessage, false)
          }
        } catch (error) {
          trackErrorInInstana({
            errorReport: 'eMandate Status Update - Error (Quick Add Flow)',
            errorData: error,
          })
        } finally {
          overlayState.hideLoader()
          // Clean up URL parameters - keep only 'id' parameter
          if (window.history && window.history.replaceState) {
            const currentUrlParams = new URLSearchParams(window.location.search)
            const id = currentUrlParams.get('id')
            const cleanUrl = id
              ? `${window.location.pathname}?id=${id}`
              : window.location.pathname
            window.history.replaceState({}, document.title, cleanUrl)
          }
        }
      }
    } catch (error) {
      console.error('Error handling eMandate redirect:', error)
      trackErrorInInstana({
        errorReport: 'eMandate Redirect Handling - Error (Quick Add Flow)',
        errorData: error,
      })
    }
  }

  initializeData = async () => {
    this.getNextGentTranslationContent()

    await Promise.all([
      multipleSubscriptionContainer.getAllSubscriptionsList().then(() => {
        const hasSubscriptionItems =
          multipleSubscriptionContainer.allSubscriptionListResponse
            ?.subscriptions?.length > 0
        return customerContainer.getPaymentsForDashboard(
          false,
          1,
          hasSubscriptionItems
        )
      }),
      addressContainer.getProfileAddress(),
    ])
  }

  setSubscriptionsName = (name, isManagePage) => {
    if (name !== '' && name !== undefined && name !== null) {
      this.subscriptionsName = name
    }
    this.isManagePage = isManagePage
  }
  renderSearch = data => {
    return (
      <SubscriptionInfiniteScrollV2
        data={data}
        isFromMultipleSubscriptions={true}
      />
    )
  }
  renderMultiVariant = data => {
    const { subscriptionItem } = data.bundleData
    const bundleItems = subscriptionContainer.getBundleData(subscriptionItem)
    data.bundleData.bundleItems = { ...bundleItems }
    return (
      <EditBundleMultiVariants {...data} isFromMultipleSubscriptions={true} />
    )
  }
  showNotifyDirectSellerPopup = async productData => {
    await cartContainer.setSkuDetailsForNotifyDirectSeller(productData)
    cartContainer.onNotifyDirectSellerSuccess = async () => {
      if (productData?.subscriptionId && productData?.itemId) {
        await multipleSubscriptionContainer.deleteSubscriptionDashboardItem(
          productData.subscriptionId,
          productData.itemId
        )
        this.microFrontendKey = Date.now()
      }
    }
    await cartContainer.toggleNotifyDirectSellerPopup()
  }

  render() {
    const myAccountSubscription = i18nTranslate(
      'myAccountSubscriptionsPage.title',
      'Nu Skin | Subscriptions'
    )
    const subscriptionText = i18nTranslate(
      'subscription.header',
      'Subscriptions'
    )

    const remoteConfig = {
      appName: 'subscription',
    }

    const isToShowOverrideSubscription = isToEnableSubscriptionOverride()
    const enableSkipSubscriptionOrderForOverride =
      isToSkipSubscriptionOrderForOverride()
    const getBlackoutDatesConfig = getBlackoutDates()
    const mysiteSponsor = getMysiteSponsor() ?? ''
    const marketTimeZone = getTimeZone() || 'US/Mountain'

    const subscriptionLimitConfig = getSubscriptionLimitConfig()
    const subscriptionAuthorizedLimit = getSubscriptionAuthorizedLimit()
    const PICKUP_SHIPPING_METHOD_ID =
      APPConfig?.getAppConfig()?.pickupShippingMethodId || 'PickUpPoint'

    const isToShowMultiVariant = enableSubscriptionMultiVariant() || false
    const taxConfig = APPConfig?.getAppConfig()?.taxConfig || {}
    const loyaltyConfig = APPConfig?.getAppConfig()?.loyaltyConfig || {}
    const storefrontLoyaltyConfig = storeContainer.getLoyaltyCSMConfig() || {}
    const enableLoyaltyPoints = storeContainer.getLoyaltyPointsConfig() || false
    const activeStoreData = {
      defaultCurrency:
        storeContainer?.activeStoreResponse?.defaultCurrency || '',
      defaultLocale: storeContainer?.activeStoreResponse?.defaultLocale || '',
      isToEnableSubscriptionOverride: isToShowOverrideSubscription,
      skipSubscriptionOrderForOverride: enableSkipSubscriptionOrderForOverride,
      isToShowMultiVariantData: isToShowMultiVariant,
      blackoutDates: getBlackoutDatesConfig,
      enableMysiteRedirection: isEquinoxMysite(),
      mySiteSponsor: mysiteSponsor,
      timeZone: marketTimeZone,
      subscriptionsConfig: subscriptionLimitConfig,
      subscriptionAuthorizedLimit,
      taxConfig,
      loyaltyConfig,
    }
    const subscriptionCSMConfig =
      storeContainer.getSubscriptionCSMConfig() || []
    const isToEnableOneTimeFrequency = convertToBoolean(
      APPConfig?.getAppConfig()?.oneTimeFrequencyEnabled === 'true'
    )
    const itemLevelSkipEnabled = convertToBoolean(
      APPConfig?.getAppConfig()?.enableItemLevelSkip === 'true'
    )
    const scanCardInSubs = isFeatureEnabled('scanCardInSubs')
    const metapackEnabled = isFeatureEnabled('metapackIntegration')
    const loyaltyVoucherEnabled = isFeatureEnabled('globalLoyalty')
    const enableNarrowSymbolCurrencyFormat = isFeatureEnabled(
      'enableNarrowSymbolCurrencyFormat'
    )
    const enableDecimalRoundingInPrice = isFeatureEnabled(
      'enableDecimalRoundingInPrice'
    )
    const excludeSpaceInPrice = isFeatureEnabled('excludeSpaceInPrice')
    const enablePromocodeDisplay = isFeatureEnabled('enablePromocodeDisplay')

    const accountProperties = accountsContainer?.profileResponse?.properties
    const subRankProperty = accountProperties?.find(
      property => property?.attributeId === 'subRank'
    )
    const subRank = Number(subRankProperty?.value) || 0

    const params = {
      SearchComponent: this.renderSearch,
      MultiVariantComponent: this.renderMultiVariant,
      setSubscriptionsName: this.setSubscriptionsName,
      subscriptionCSMConfig,
      customerType: customerContainer.profileResponse?.accounts?.type || '',
      activeStoreResponse: activeStoreData || {},
      allowedSubscriptionFrequency: getAllowedSubscriptionFrequency(),
      oneTimeFrequencyEnabled: isToEnableOneTimeFrequency,
      enableItemLevelSkip: itemLevelSkipEnabled,
      hideScanCardOption: scanCardInSubs,
      showNotifyDirectSellerPopup: this.showNotifyDirectSellerPopup,
      enableMetaPack: metapackEnabled,
      enableNarrowSymbolCurrencyFormat,
      enableDecimalRoundingInPrice,
      excludeSpaceInPrice,
      enableGlobalLoyalty: loyaltyVoucherEnabled,
      loyaltyConfig: storefrontLoyaltyConfig,
      pickupShippingMethodId: PICKUP_SHIPPING_METHOD_ID,
      preLOI:
        customerContainer?.profileResponse?.customProperties?.preLOI || 'false',
      enablePromocodeDisplay: enablePromocodeDisplay,
      subRank: subRank,
      enableLoyaltyPoints: enableLoyaltyPoints,
      showAllPromoCodesInCheckout: showAllPromoCodesInCheckout(),
    }

    const navUrl = `${pageNames?.myaccountsubscriptions}`

    const breadCrumbList = [
      {
        label: i18nTranslate(
          'subscriptionManageHeader.accountNavName',
          'Account',
          { isNextGenTranslations: true }
        ),
        value: `${pageNames?.b2bMyProfile}`,
        isLastElement: false,
      },
      {
        label: i18nTranslate('common.subscriptions', 'Subscriptions', {
          isNextGenTranslations: true,
        }),
        value: navUrl,
        isLastElement: !this.isManagePage,
      },
    ]
    if (this.isManagePage) {
      const list = {
        label: i18nTranslate(
          'subscriptionManageHeader.pageName',
          'Manage %subscription_name%',
          { isNextGenTranslations: true }
        ).replace('%subscription_name%', this.subscriptionsName),
        value: '',
        isLastElement: true,
      }
      breadCrumbList.push(list)
    }
    const isSubscriptionTranslationsLoaded =
      Object.keys(nextGenTranslationContent.nextGenTranslationContentValue)
        .length > 0

    if (!isSubscriptionTranslationsLoaded) {
      return <></>
    }
    return (
      <>
        <div key={this.props?.location?.pathname || 'default'}>
          <MetaDecorator
            title={myAccountSubscription}
            imageUrl=""
            imageAlt=""
            description={myAccountSubscription}
            location={this.props}
          />
          <div
            ref={this.containerRef}
            tabIndex={-1}
            className="multiple-subscriptions-container"
            data-testid="multiple-subscriptions-container">
            <BreadCrumb
              breadCrumbList={breadCrumbList}
              location={this.props?.location}
            />
            {!this.isManagePage && (
              <h1
                className="heading-1 multiple-subscriptions-title"
                data-testid="qa-subscriptions-title">
                {subscriptionText}
              </h1>
            )}
            <MicroFrontendLoader
              key={this.microFrontendKey}
              remoteConfig={remoteConfig}
              params={params}
            />
            <SubscriptionModal
              history={this.props.history || {}}
              isGPS3DSEnabledInSubscription={this.isGPS3DSEnabledInSubscription}
              triggerSubscription3DSInitiateCall={
                this.triggerSubscription3DSInitiateCall
              }
              getGlobalPaymentOptionsForSubscription={
                this.getGlobalPaymentOptionsForSubscription
              }
              globalPaymentResponse={this.globalPaymentResponse}
            />
          </div>
        </div>
      </>
    )
  }
}
export default MultipleSubscriptions
export { MultipleSubscriptions }
